import type { FaceShapeContent, FaceShapeId } from '../types';

export const SHAPE_ORDER: FaceShapeId[] = [
  'oval',
  'round',
  'square',
  'oblong',
  'heart',
  'diamond',
  'triangle',
];

export const FACE_SHAPES: Record<FaceShapeId, FaceShapeContent> = {
  oval: {
    id: 'oval',
    label: 'Oval',
    shortLabel: 'Oval',
    summary: 'Longer than wide, with the cheekbones subtly leading and both the forehead and jaw tapering softly.',
    signature: 'Balanced length, gentle taper, softly curved jaw',
    oftenConfusedWith: ['oblong', 'round'],
    advice: {
      hair: [
        {
          title: 'Keep the natural balance visible',
          why: 'Most lengths, parts, and textures work because the forehead, cheekbones, and jaw already transition gradually.',
          useCarefully: 'Very heavy forward fringe can hide the balanced vertical proportion that defines an oval result.',
        },
        {
          title: 'Use texture as the focal point',
          why: 'Waves, curls, crops, layers, and clean geometric cuts can all sit comfortably on a balanced outline.',
          useCarefully: 'Extreme height plus very flat sides can make the face read more oblong than it is.',
        },
      ],
      glasses: [
        {
          title: 'Match frame width to the cheekbones',
          why: 'Frames that are approximately as wide as the broadest part of the face preserve the existing balance.',
          useCarefully: 'Frames much narrower than the cheekbones can look visually undersized.',
        },
        {
          title: 'Choose character, not correction',
          why: 'Rectangular, round, aviator, browline, and softly angular frames can all work; the bridge and fit matter more than a single shape rule.',
          useCarefully: 'Oversized frames should still keep the eyes centered and avoid resting on the cheeks.',
        },
      ],
      beard: [
        {
          title: 'Follow the existing jaw taper',
          why: 'Short stubble, a neat full beard, or a clean outline can reinforce an already balanced lower face.',
          useCarefully: 'A very long narrow point at the chin can overextend the face visually.',
        },
        {
          title: 'Keep side and chin volume proportional',
          why: 'Even distribution maintains the oval relationship instead of pulling the shape wider or longer.',
          useCarefully: 'Large side volume with a very short chin can make the result appear rounder.',
        },
      ],
      makeup: [
        {
          title: 'Place emphasis where you want attention',
          why: 'An oval outline generally does not need strong corrective placement, so blush, highlight, and color can follow personal preference.',
          useCarefully: 'Heavy contour on every edge can flatten the natural transitions visible in the measurements.',
        },
        {
          title: 'Echo the cheekbone line',
          why: 'Soft placement along the cheekbone keeps the face’s broadest area visually coherent.',
          useCarefully: 'Very horizontal placement can make the face look wider; very vertical placement can make it look longer.',
        },
      ],
    },
  },
  round: {
    id: 'round',
    label: 'Round',
    shortLabel: 'Round',
    summary: 'Face length and width are relatively close, with soft transitions and a rounded lower-face outline.',
    signature: 'Similar length and width, soft jaw, full curved sides',
    oftenConfusedWith: ['square', 'oval'],
    advice: {
      hair: [
        {
          title: 'Create a little vertical direction',
          why: 'Height at the crown, a longer front, or layers below the cheekbone can visually extend the face.',
          useCarefully: 'A one-length cut ending at the widest cheek area can emphasize width if that is not your goal.',
        },
        {
          title: 'Use asymmetry to add structure',
          why: 'Side parts, diagonal fringe, and off-center texture interrupt an otherwise even circular rhythm.',
          useCarefully: 'Dense rounded volume at both sides can repeat the same curve as the face outline.',
        },
      ],
      glasses: [
        {
          title: 'Introduce a clear corner or lifted edge',
          why: 'Rectangular, wayfarer, browline, and gently upswept frames add directional contrast to soft contours.',
          useCarefully: 'Very small circular frames can echo the face outline rather than define it.',
        },
        {
          title: 'Look for a frame slightly wider than the cheeks',
          why: 'A modest outward extension creates a cleaner horizontal boundary without overwhelming the face.',
          useCarefully: 'Deep frames that sit on the cheeks may move when you smile; fit remains more important than category.',
        },
      ],
      beard: [
        {
          title: 'Keep the sides controlled and add length below',
          why: 'A slightly longer chin area introduces vertical direction while a clean side outline reduces extra width.',
          useCarefully: 'A uniformly wide rounded beard can amplify the circular silhouette.',
        },
        {
          title: 'Define the cheek and neck lines',
          why: 'Crisp boundaries can add structure even with short stubble or a compact beard.',
          useCarefully: 'An extremely high cheek line may reduce the visible height of the face.',
        },
      ],
      makeup: [
        {
          title: 'Use diagonal placement',
          why: 'Blush and contour angled slightly upward can introduce direction across a soft, even outline.',
          useCarefully: 'A round spot of color centered on the apples of the cheeks can emphasize fullness.',
        },
        {
          title: 'Keep the center softly illuminated',
          why: 'A restrained highlight through the center can support a longer visual line.',
          useCarefully: 'Strong shimmer across the entire cheek width may broaden the face in photographs.',
        },
      ],
    },
  },
  square: {
    id: 'square',
    label: 'Square',
    shortLabel: 'Square',
    summary: 'Forehead, cheek, and jaw widths are close, while the jaw corners read more angular than curved.',
    signature: 'Even widths, defined jaw corners, compact length',
    oftenConfusedWith: ['round', 'oblong'],
    advice: {
      hair: [
        {
          title: 'Add movement around the corners',
          why: 'Layers, waves, curls, and side-swept direction soften the transition from cheek to jaw without hiding it.',
          useCarefully: 'A blunt edge ending exactly at the jaw can repeat its strongest horizontal line.',
        },
        {
          title: 'Choose either confident geometry or soft contrast',
          why: 'A precise crop can celebrate the jaw, while longer texture can create a more fluid outline; both are coherent choices.',
          useCarefully: 'Flat width at the temples plus width at the jaw can make the face appear broader.',
        },
      ],
      glasses: [
        {
          title: 'Try curves with a strong bridge',
          why: 'Round, oval, aviator, or softly upswept frames contrast the jaw while retaining enough structure to feel intentional.',
          useCarefully: 'Very boxy frames that exactly mirror the face width can make every line compete.',
        },
        {
          title: 'Use moderate frame depth',
          why: 'A little vertical depth balances a compact length-to-width ratio.',
          useCarefully: 'Ultra-shallow frames can emphasize the strongest horizontal proportions.',
        },
      ],
      beard: [
        {
          title: 'Soften or celebrate the jaw deliberately',
          why: 'A rounded lower edge softens the corners; short structured stubble emphasizes them. Both work when the outline is clean.',
          useCarefully: 'Maximum width at the jaw with no chin length can make the lower face look heavier.',
        },
        {
          title: 'Add modest length at the center',
          why: 'A slightly extended chin can balance near-equal face length and width.',
          useCarefully: 'A very narrow point may feel disconnected from a broad angular jaw.',
        },
      ],
      makeup: [
        {
          title: 'Blend around the jaw corners',
          why: 'Soft diffusion at the outer forehead and jaw can reduce the visual weight of the strongest angles when desired.',
          useCarefully: 'Hard contour stripes can make the geometry look more rigid in direct light.',
        },
        {
          title: 'Use curved placement on the cheeks',
          why: 'Rounded blush or highlight placement introduces a softer counter-shape.',
          useCarefully: 'A straight horizontal band can repeat the face’s widest lines.',
        },
      ],
    },
  },
  oblong: {
    id: 'oblong',
    label: 'Oblong',
    shortLabel: 'Oblong',
    summary: 'The face is noticeably longer than wide, with forehead, cheek, and jaw widths staying relatively consistent.',
    signature: 'Extended length, parallel sides, softly defined jaw',
    oftenConfusedWith: ['oval', 'square'],
    advice: {
      hair: [
        {
          title: 'Build width beside the cheeks',
          why: 'Waves, curls, side volume, and layers near the cheek and jaw interrupt a long vertical line.',
          useCarefully: 'Very tall crown volume with close-cut sides can extend the face further.',
        },
        {
          title: 'Use fringe or a lower part to shorten the visible field',
          why: 'A fringe, curtain shape, or side-swept front can reduce the amount of uninterrupted forehead-to-chin length.',
          useCarefully: 'Long, pin-straight hair with no side movement can repeat the same vertical proportion.',
        },
      ],
      glasses: [
        {
          title: 'Choose frames with visible depth',
          why: 'Taller lenses, aviators, and substantial brow lines occupy more vertical space and visually divide the face.',
          useCarefully: 'Very narrow shallow frames may look small against a longer face.',
        },
        {
          title: 'Let the frame extend confidently outward',
          why: 'Width at the temples creates a horizontal counterpoint to the measured length.',
          useCarefully: 'A frame that is both narrow and delicate may not provide enough visual balance.',
        },
      ],
      beard: [
        {
          title: 'Keep chin length compact and allow side fullness',
          why: 'More width at the sides and less extension below the chin can shorten the visual line.',
          useCarefully: 'A long pointed beard adds directly to the face’s strongest dimension.',
        },
        {
          title: 'Use a rounded or squared lower edge',
          why: 'A defined horizontal finish creates a stopping point beneath the chin.',
          useCarefully: 'A taper that keeps narrowing downward can overemphasize length.',
        },
      ],
      makeup: [
        {
          title: 'Favor horizontal cheek placement',
          why: 'Blush and highlight carried slightly outward create width and break up the vertical line.',
          useCarefully: 'Steep diagonal placement can add more perceived length.',
        },
        {
          title: 'Frame the top and bottom subtly',
          why: 'Soft shading near the upper forehead and lower chin can visually shorten the face if desired.',
          useCarefully: 'A bright uninterrupted center stripe can reinforce length in photographs.',
        },
      ],
    },
  },
  heart: {
    id: 'heart',
    label: 'Heart',
    shortLabel: 'Heart',
    summary: 'The upper face is broader than the jaw, and the lower face narrows into a visibly tapered chin.',
    signature: 'Broad forehead, high cheek area, narrow tapered jaw',
    oftenConfusedWith: ['diamond', 'oval'],
    advice: {
      hair: [
        {
          title: 'Place movement around the jaw',
          why: 'Chin-length texture, waves below the cheekbones, and lower layers add visual weight where the measured outline narrows.',
          useCarefully: 'Maximum width at the temples with very little below can repeat the top-heavy proportion.',
        },
        {
          title: 'Use a soft side or curtain direction',
          why: 'Diagonal movement across the forehead reduces a strong upper horizontal line and leads the eye toward the center.',
          useCarefully: 'A very short blunt fringe can make the forehead appear wider.',
        },
      ],
      glasses: [
        {
          title: 'Try frames with a lighter or wider lower half',
          why: 'Aviators, rimless styles, soft ovals, and subtle bottom detail can balance a narrower jaw.',
          useCarefully: 'A very heavy brow-only frame can concentrate even more visual weight at the top.',
        },
        {
          title: 'Keep the temples from overextending',
          why: 'A frame close to cheekbone width respects the broad upper face without adding unnecessary width.',
          useCarefully: 'Strong outward flares can exaggerate the forehead-to-jaw difference.',
        },
      ],
      beard: [
        {
          title: 'Add controlled fullness through the jaw',
          why: 'A short boxed beard or fuller lower sides can support the narrower lower-face measurement.',
          useCarefully: 'A narrow pointed goatee can repeat the chin taper too strongly.',
        },
        {
          title: 'Keep the chin base broad rather than sharp',
          why: 'A softly squared or rounded lower edge gives the jaw more visual substance.',
          useCarefully: 'Very short sides with all length centered at the chin can increase the triangular effect.',
        },
      ],
      makeup: [
        {
          title: 'Carry color slightly lower and outward',
          why: 'Cheek placement that moves toward the jaw distributes attention away from the widest upper area.',
          useCarefully: 'Strong highlight only at the temples can emphasize upper-face width.',
        },
        {
          title: 'Use a restrained forehead contour',
          why: 'Soft shading near the outer forehead can create balance while preserving the natural cheek structure.',
          useCarefully: 'A dark, hard hairline band can look disconnected in changing light.',
        },
      ],
    },
  },
  diamond: {
    id: 'diamond',
    label: 'Diamond',
    shortLabel: 'Diamond',
    summary: 'The cheekbones are clearly the widest point, while both the forehead and jaw narrow away from them.',
    signature: 'Prominent cheek width, narrow forehead, tapered jaw',
    oftenConfusedWith: ['heart', 'oval'],
    advice: {
      hair: [
        {
          title: 'Add softness at the forehead and jaw',
          why: 'Fringe, temple texture, and volume near the chin balance a contour that peaks at the cheekbones.',
          useCarefully: 'Maximum width centered directly at the cheekbone can amplify the widest point.',
        },
        {
          title: 'Use tuck, part, and texture strategically',
          why: 'A side part or one-sided tuck can reveal the cheekbone while keeping the overall outline fluid.',
          useCarefully: 'Very tight sides above and below the cheek can make the center width feel abrupt.',
        },
      ],
      glasses: [
        {
          title: 'Follow the cheek line with gentle lift',
          why: 'Oval, rimless, cat-eye, and softly curved browline frames complement prominent cheekbones without adding harsh corners.',
          useCarefully: 'A frame much wider than the cheekbones can look unstable on a narrower temple area.',
        },
        {
          title: 'Give the brow a little presence',
          why: 'Subtle top detail adds visual width near a narrower forehead and balances the center of the face.',
          useCarefully: 'A very narrow bridge or tiny lens may feel pinched against strong cheek width.',
        },
      ],
      beard: [
        {
          title: 'Build a stable lower outline',
          why: 'Moderate jaw fullness and a rounded or squared chin can balance a narrow lower face beneath broad cheeks.',
          useCarefully: 'A sharply pointed beard can exaggerate the taper below the cheekbones.',
        },
        {
          title: 'Keep cheek lines natural',
          why: 'A slightly lower, softer cheek line avoids creating another hard diagonal beside prominent cheekbones.',
          useCarefully: 'A steeply cut high cheek line can make the mid-face look even wider.',
        },
      ],
      makeup: [
        {
          title: 'Diffuse rather than sharpen the cheek peak',
          why: 'Soft blush placement just below or around the cheekbone keeps the central width from becoming too angular.',
          useCarefully: 'A narrow bright stripe directly on the highest point can intensify the diamond geometry.',
        },
        {
          title: 'Share light with the forehead and jaw',
          why: 'Subtle highlight at the center forehead and chin distributes attention across the full vertical outline.',
          useCarefully: 'Heavy shading on both the forehead and jaw can make them look narrower still.',
        },
      ],
    },
  },
  triangle: {
    id: 'triangle',
    label: 'Triangle',
    shortLabel: 'Triangle',
    summary: 'The jaw is the widest region, while the cheek and forehead measurements become progressively narrower above it.',
    signature: 'Broad jaw, narrower cheeks, narrow forehead',
    oftenConfusedWith: ['square', 'heart'],
    advice: {
      hair: [
        {
          title: 'Create width through the crown and temples',
          why: 'Upper volume, side-swept texture, and fuller temple areas balance the jaw-led lower outline.',
          useCarefully: 'A cut that is very flat on top and widest at the jaw can repeat the same triangle.',
        },
        {
          title: 'Keep the jaw area light or mobile',
          why: 'Layers that move past the jaw avoid adding a fixed horizontal block at the broadest point.',
          useCarefully: 'A blunt heavy edge ending exactly at the jaw may make it look wider.',
        },
      ],
      glasses: [
        {
          title: 'Use a confident upper frame line',
          why: 'Browline, cat-eye, and frames with upper-corner detail add visual structure where the forehead is narrower.',
          useCarefully: 'Bottom-heavy frames can add more weight near the widest lower region.',
        },
        {
          title: 'Choose enough width at the temples',
          why: 'A frame that reaches toward the upper face’s ideal width creates balance against the jaw.',
          useCarefully: 'Tiny narrow frames can make the difference between forehead and jaw look larger.',
        },
      ],
      beard: [
        {
          title: 'Reduce side bulk and keep the center intentional',
          why: 'Shorter sides prevent extra width at the jaw, while moderate chin length introduces vertical direction.',
          useCarefully: 'A very wide boxed beard can amplify the strongest measurement.',
        },
        {
          title: 'Use a clean tapered lower edge',
          why: 'A gradual move toward the center can make the lower face feel less block-like.',
          useCarefully: 'An extremely sharp point may overcorrect and look disconnected from a broad jaw.',
        },
      ],
      makeup: [
        {
          title: 'Bring emphasis upward',
          why: 'Light and color at the temples, upper cheeks, or eyes distribute attention toward the narrower top half.',
          useCarefully: 'Strong low cheek color can add visual weight close to the jaw.',
        },
        {
          title: 'Soften the outer jaw selectively',
          why: 'A diffused contour near the broadest corners can create a smoother transition into the cheeks.',
          useCarefully: 'A hard dark stripe can make the jaw boundary look heavier rather than softer.',
        },
      ],
    },
  },
};

export function getShapeContent(id: FaceShapeId): FaceShapeContent {
  return FACE_SHAPES[id];
}
